import { useEffect } from "react";

const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://blessingsconnection.com").replace(/\/$/, "");
const SITE_NAME = "Blessings Connection";
const DEFAULT_TITLE = "Blessings Connection — Trusted Online Bookstore for New & Academic Books";
const DEFAULT_DESCRIPTION =
  "Shop new books, textbooks, and educational materials at Blessings Connection LLC. Fast U.S. shipping, secure checkout, and 15-day easy returns.";

const setMeta = (name, content, useProperty = false) => {
  if (!content) return;
  const attr = useProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(content));
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, json) => {
  let el = document.head.querySelector(`script[type="application/ld+json"][data-seo="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(json);
};

/**
 * Apply per-page SEO. Handles document.title, meta description, meta robots,
 * Open Graph, Twitter, canonical, and JSON-LD structured data.
 * Call from a useEffect that depends on the page's data.
 */
export function useSeo({
  title,
  description,
  canonicalPath,
  image,
  jsonLd,
  noindex = false,
  ogType = "website",
} = {}) {
  useEffect(() => {
    const finalTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
    const finalDesc = description || DEFAULT_DESCRIPTION;
    const finalCanonical = canonicalPath
      ? `${SITE_URL}${canonicalPath.startsWith("/") ? canonicalPath : "/" + canonicalPath}`
      : SITE_URL;
    const finalImage = image || `${SITE_URL}/og-default.jpg`;

    document.title = finalTitle;
    setMeta("description", finalDesc);
    setMeta("robots", noindex ? "noindex,nofollow" : "index,follow");
    setCanonical(finalCanonical);

    // Open Graph
    setMeta("og:site_name", SITE_NAME, true);
    setMeta("og:title", finalTitle, true);
    setMeta("og:description", finalDesc, true);
    setMeta("og:type", ogType, true);
    setMeta("og:url", finalCanonical, true);
    setMeta("og:image", finalImage, true);

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", finalTitle);
    setMeta("twitter:description", finalDesc);
    setMeta("twitter:image", finalImage);

    // JSON-LD (single or array)
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item, i) => setJsonLd(`page-${i}`, item));
      // Remove any leftover ld-json from a previous page with more entries
      let idx = items.length;
      while (document.head.querySelector(`script[data-seo="page-${idx}"]`)) {
        document.head.querySelector(`script[data-seo="page-${idx}"]`).remove();
        idx += 1;
      }
    } else {
      document.head.querySelectorAll('script[type="application/ld+json"][data-seo^="page-"]').forEach((n) => n.remove());
    }
  }, [title, description, canonicalPath, image, JSON.stringify(jsonLd), noindex, ogType]);
}

export { SITE_URL, SITE_NAME };
